/**
 * User by Phone hook using React Relay
 *
 * This hook fetches a user by phone number since the current URL routing
 * uses phone as the user identifier. It uses listUsers with a phone filter
 * and then fetches full details.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useUserByPhoneQuery as UseUserByPhoneQuery } from './__generated__/useUserByPhoneQuery.graphql';
import type { useUserByPhoneOrgUserSystemsQuery as UseOrgUserSystemsQuery } from './__generated__/useUserByPhoneOrgUserSystemsQuery.graphql';
import type { UserDetailGraphQL } from './useUserDetailRelay';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for user by phone with all detail fields
 */
const UserByPhoneQuery = graphql`
  query useUserByPhoneQuery(
    $filter: UserFilterInput!
    $isOrgContext: Boolean!
    $userSystemFilter: UserSystemFilterInput
    $walletFilter: UserWalletFilterInput
    $membershipFilter: UserMembershipFilterInput
    $errorReportFilter: SupportTicketFilterInput
  ) {
    listUsers(first: 1, filter: $filter) {
      edges {
        node {
          # Core fields
          id
          phone
          firstName
          lastName
          fullName
          createdOn
          deletedOn
          delinquent
          authStatus
          language
          region
          # Org/system ID lists are only consumed by the organization view
          organizationIds @include(if: $isOrgContext)
          systemIds @include(if: $isOrgContext)

          # Account info
          email
          autoGenNumber
          creditCards

          # Device info
          latestDevice {
            appVersion
            model
            os
            osVersion
            carrier
            brand
            manufacturer
          }

          # User systems - scoped to the current system (or organization)
          userSystems(filter: $userSystemFilter) {
            id
            blacklisted
            hidden
            joined
            rejected
            removed
            acceptedTosOn
            systemId
            organizationId
            amountDue
          }

          # RFID cards
          rfidCards {
            id
            cardId
            label
            isActive
            archivedOn
            createdOn
          }

          # Memberships
          userMemberships(filter: $membershipFilter) {
            id
            oldId
            approvedOn
            canceledOn
            createdOn
            expiresOn
            rejectedOn
            isActive
            isApproved
            isCanceled
            isExpired
            isRejected
            priority
            membershipOldId
            membership {
              id
              oldId
              name
              authType
              archivedOn
              systemId
              systemOldId
            }
          }

          # Wallets - system context only, scoped to the system's partner
          userWallets(filter: $walletFilter) @skip(if: $isOrgContext) {
            id
            balance
            currency
            paymentProvider
            partnerOldId
          }

          # Error reports (problems)
          errorReports(
            limit: 50
            sort: [{ field: CREATED, order: DESC }]
            filter: $errorReportFilter
          ) {
            id
            oldId
            content
            created
            closedDate
            state
            type
            subtype
            notes
            owner
            hardwareOldId
            rentalOldId
            systemOldId
            tags
          }
        }
      }
    }

  }
`;

/**
 * Resolves the systems the user belongs to within an organization.
 * Uses OrganizationSystem which has a nested `system` relation with name/oldId.
 * Called once after the main query returns the user's ID.
 */
const OrgUserSystemsQuery = graphql`
  query useUserByPhoneOrgUserSystemsQuery(
    $filter: OrganizationSystemFilterInput!
  ) {
    listOrganizationSystems(first: 100, filter: $filter) {
      edges {
        node {
          systemId
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

// Re-export types from useUserDetailRelay for convenience
export type {
  UserDetailGraphQL,
  UserSystemDetailGraphQL,
  RfidCardGraphQL,
  UserMembershipGraphQL,
  UserWalletGraphQL,
  UserRentalGraphQL,
  ErrorReportGraphQL,
} from './useUserDetailRelay';

/**
 * System info fetched in org context (from listSystems filter by organizationId).
 * Used to resolve system name / oldId for entries in user.userSystems.
 */
export interface OrganizationSystemGraphQL {
  id: string;
  oldId?: string | null;
  name?: string | null;
  organizationId?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching a user by phone number
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch } = useUserByPhoneQuery('+1234567890');
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.fullName}</div>;
 */
export function useUserByPhoneQuery(
  phone: string | undefined,
  options?: {
    enabled?: boolean;
    onError?: (error: Error) => void;
    systemId?: string;
    organizationId?: string;
    partnerOldId?: number | null;
  }
) {
  const [data, setData] = useState<UserDetailGraphQL | null>(null);
  const [organizationSystems, setOrganizationSystems] = useState<
    OrganizationSystemGraphQL[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(phone);

  // Use refs to avoid recreating doFetch on every render
  const isFetchedRef = useRef(isFetched);
  const onErrorRef = useRef(options?.onError);
  const systemIdRef = useRef(options?.systemId);
  const organizationIdRef = useRef(options?.organizationId);
  const partnerOldIdRef = useRef(options?.partnerOldId);

  // Keep refs in sync
  useEffect(() => {
    isFetchedRef.current = isFetched;
  }, [isFetched]);

  useEffect(() => {
    onErrorRef.current = options?.onError;
  }, [options?.onError]);

  useEffect(() => {
    systemIdRef.current = options?.systemId;
  }, [options?.systemId]);

  useEffect(() => {
    organizationIdRef.current = options?.organizationId;
  }, [options?.organizationId]);

  useEffect(() => {
    partnerOldIdRef.current = options?.partnerOldId;
  }, [options?.partnerOldId]);

  // Memoize the fetch function with stable dependencies
  const doFetch = useCallback(async () => {
    if (!phone) return;

    try {
      if (isFetchedRef.current) {
        setIsRefetching(true);
      } else {
        setIsLoading(true);
      }

      const organizationId = organizationIdRef.current;
      const systemId = systemIdRef.current;
      const partnerOldId = partnerOldIdRef.current;
      const isOrgContext = Boolean(organizationId);

      const result = await fetchQuery<UseUserByPhoneQuery>(
        relayEnvironment,
        UserByPhoneQuery,
        {
          filter: {
            phone: { eq: phone },
          },
          isOrgContext,
          // Scope per-system rows to the org (all of the user's systems within it)
          // or to the single system being viewed.
          userSystemFilter: isOrgContext
            ? { organizationId: { eq: organizationId } }
            : systemId
            ? { systemId: { eq: systemId } }
            : undefined,
          // Wallets are only fetched in system context (@skip in org context).
          // Scope them to the system's partner; when no partner is known, fall
          // back to all wallets so the mapper can pick the first one.
          walletFilter:
            !isOrgContext && partnerOldId != null
              ? { partnerOldId: { eq: partnerOldId } }
              : undefined,
          membershipFilter: isOrgContext
            ? { organizationId: { eq: organizationId } }
            : systemId
            ? {
                membership: {
                  perks: { system: { id: { eq: systemId } } },
                },
              }
            : undefined,
          errorReportFilter: isOrgContext
            ? { organizationId: { eq: organizationId } }
            : systemId
            ? { systemId: { eq: systemId } }
            : undefined,
        }
      ).toPromise();

      if (result) {
        const user = result.listUsers?.edges?.[0]?.node ?? null;

        // In org context, resolve the user's systems before setting state
        // so both data and organizationSystems update in the same render.
        let resolvedSystems: OrganizationSystemGraphQL[] = [];
        if (isOrgContext && user) {
          const userSystemIds = (user.userSystems ?? [])
            .filter(
              (us) => us.organizationId === organizationId && us.systemId
            )
            .map((us) => us.systemId as string);

          if (userSystemIds.length > 0) {
            const orgSysResult =
              await fetchQuery<UseOrgUserSystemsQuery>(
                relayEnvironment,
                OrgUserSystemsQuery,
                {
                  filter: {
                    organizationId: { eq: organizationId },
                    systemId: { in: userSystemIds },
                  },
                }
              ).toPromise();

            resolvedSystems = (
              orgSysResult?.listOrganizationSystems?.edges ?? []
            )
              .map((edge) => edge?.node)
              .filter(Boolean)
              .map((node) => ({
                id: node!.system.id,
                oldId: node!.system.oldId ?? null,
                name: node!.system.name ?? null,
                organizationId: organizationId ?? null,
              }));
          }
        }

        // Batch state updates together
        setData(user as UserDetailGraphQL | null);
        setOrganizationSystems(resolvedSystems);
        setError(null);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch user');
      setError(errorObj);
      onErrorRef.current?.(errorObj);
    } finally {
      setIsLoading(false);
      setIsFetched(true);
      setIsRefetching(false);
    }
  }, [phone]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    doFetch();
  }, [enabled, phone, fetchCount, doFetch]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
    return doFetch().then(() => ({ data }));
  }, [doFetch, data]);

  return useMemo(
    () => ({
      data,
      organizationSystems,
      isLoading,
      isFetched,
      isRefetching,
      error,
      refetch,
    }),
    [data, organizationSystems, isLoading, isFetched, isRefetching, error, refetch]
  );
}
